// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Column definitions for the contract review dashboard table.
 *
 * Layout: Doc ID | Uploaded By | Document | [Renderer columns...] | Agents | Actions
 * Renderer columns are dynamic — one per renderer defined on the workspace.
 * A renderer with columnType === 'risk' renders a score badge read from the
 * shared analysis-status map; all others render the generic eye-icon cell.
 */
import { useMemo } from 'react'
import { Badge } from '@/components/ui/badge'
import type { DashboardTableColumn } from '../components/DashboardTable'
import type { DocumentListItemWithPipelines, PipelineOption } from '../models'
import type { DocumentPreviewPayload } from '../components/preview/PreviewContext'
import type { NodeApiClient } from '@/app-shell/services'
import type { WorkspaceRenderer } from '@/subsystems/agents'
import type { UseAnalysisStatusesResult } from '@/applications/shared'

import { DocumentCell } from '../components/cells/DocumentCell'
import { ActionsCell } from '../components/cells/ActionsCell'
import { RendererCell } from '../components/cells/RendererCell'
import { RunCell } from '../components/cells/RunCell'
import { getRendererComponent } from '@/app-shell/renderer-components'

export interface UseDashboardColumnsParams {
  availablePipelines: PipelineOption[]
  /** Renderer definitions keyed by renderer ID */
  renderers: Record<string, WorkspaceRenderer>
  analysisStatuses: UseAnalysisStatusesResult
  running: boolean
  resolvedOrgId: string
  wsId: string
  basePath: string | null
  apiClient: NodeApiClient
  onPreviewRequest: (payload: DocumentPreviewPayload) => void
  onRunDocument: (documentId: string, pipelineId?: string, targetVersion?: number) => void
  onRunComparison: (documentId: string, pipelineId: string, oldVersion: number, newVersion: number, datasetKey?: string) => void
  onViewComparison: (documentId: string, oldVersion: number, newVersion: number, agentId: string, datasetKey?: string) => void
  onOpenDocumentManager: (documentId: string) => void
  pipelineSelections: Map<string, Set<string>>
  onTogglePipelineSelection: (documentId: string, pipelineId: string) => void
  onClearPipelineSelection: (documentId: string) => void
  versionSelections: Map<string, Record<string, number>>
  onVersionChange: (documentId: string, pipelineId: string, version: number) => void
}

function renderRiskBadge(score: number | undefined, type: string | undefined) {
  if (type === 'error') {
    return (
      <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-5 font-semibold bg-destructive/10 text-destructive border-destructive/30">
        Error
      </Badge>
    )
  }
  if (score == null) {
    return <span className="text-muted-foreground">—</span>
  }
  const color =
    score >= 4 ? 'bg-destructive/10 text-destructive border-destructive/30' :
    score >= 3 ? 'bg-amber-100 text-amber-700 border-amber-300' :
    'bg-emerald-100 text-emerald-700 border-emerald-300'
  return (
    <Badge data-testid="cr-risk-score" variant="outline" className={`text-[10px] px-1.5 py-0 h-5 font-semibold ${color}`}>
      {score}
    </Badge>
  )
}

export const useDashboardColumns = (params: UseDashboardColumnsParams): DashboardTableColumn<DocumentListItemWithPipelines>[] => {
  const {
    availablePipelines,
    renderers,
    analysisStatuses,
    running,
    resolvedOrgId,
    wsId,
    basePath,
    apiClient,
    onPreviewRequest,
    onRunDocument,
    onRunComparison,
    onViewComparison,
    onOpenDocumentManager,
    pipelineSelections,
    onTogglePipelineSelection,
    onClearPipelineSelection,
    versionSelections,
    onVersionChange,
  } = params

  const rendererEntries = useMemo(
    () => Object.entries(renderers).sort(
      ([, a], [, b]) => (a.order ?? 0) - (b.order ?? 0),
    ),
    [renderers]
  )

  return useMemo<DashboardTableColumn<DocumentListItemWithPipelines>[]>(
    () => [
      {
        key: 'docId',
        header: 'Doc ID',
        className: 'w-28 hidden lg:table-cell',
        render: (document) => (
          <span className="text-sm text-muted-foreground font-mono">
            {document.documentId}
          </span>
        ),
      },
      {
        key: 'uploadedBy',
        header: 'Uploaded By',
        className: 'w-32 hidden lg:table-cell',
        render: (document) => (
          <span className="text-sm text-muted-foreground">
            {document.uploadedBy ?? '—'}
          </span>
        ),
      },
      {
        key: 'document',
        header: 'Document',
        className: 'whitespace-normal',
        render: (document) => (
          <DocumentCell
            document={document}
            orgId={resolvedOrgId}
            wsId={wsId}
            apiClient={apiClient}
          />
        ),
      },
      // Dynamic renderer columns
      ...rendererEntries.map(([rendererId, renderer]) => ({
        key: `renderer-${rendererId}`,
        header: renderer.label,
        className: 'w-16 text-center',
        render: (document: DocumentListItemWithPipelines) => {
          const datasetKey = renderer.slots.find(s => s.type === 'dataset' && s.datasetKey)?.datasetKey
          if (!datasetKey) {
            return <span className="text-muted-foreground">—</span>
          }
          const statuses = analysisStatuses.getDataset(renderer.agentId, datasetKey)

          if (renderer.columnType === 'risk') {
            return renderRiskBadge(
              statuses.scores.get(document.documentId),
              statuses.types.get(document.documentId),
            )
          }
          return (
            <RendererCell
              rendererId={rendererId}
              renderer={renderer}
              hasAnalysis={statuses.types.has(document.documentId)}
              onView={(rid) => {
                const version = document.latestVersion ?? 1
                const componentDef = getRendererComponent(renderer.component)
                if (componentDef?.versionMode === 'compare') {
                  onViewComparison(document.documentId, Math.max(1, version - 1), version, renderer.agentId, datasetKey)
                  return
                }
                onPreviewRequest({
                  documentId: document.documentId,
                  targetId: 'dashboard.viewer.panel',
                  experience: 'dashboard',
                  source: 'renderer',
                  pipelineId: rid,
                  orgId: resolvedOrgId,
                  workspaceId: wsId,
                  nodeInstanceId: null,
                  title: renderer.label,
                  version,
                  latestVersion: version,
                })
              }}
            />
          )
        },
      })),
      // Agents column — pipeline multi-select dropdown + run button
      {
        key: 'agents',
        header: 'Agents',
        className: 'whitespace-normal',
        render: (document) => (
          <RunCell
            documentId={document.documentId}
            availablePipelines={availablePipelines}
            pipelineStatuses={document.pipelineStatuses ?? []}
            onRunDocument={onRunDocument}
            onRunComparison={onRunComparison}
            running={running}
            basePath={basePath}
            apiClient={apiClient}
            latestVersion={document.latestVersion ?? 1}
            selectedPipelineIds={pipelineSelections.get(document.documentId) ?? new Set()}
            onToggleSelection={(pipelineId) => onTogglePipelineSelection(document.documentId, pipelineId)}
            onClearSelection={() => onClearPipelineSelection(document.documentId)}
            selectedVersions={versionSelections.get(document.documentId) ?? {}}
            onVersionChange={(pipelineId, version) => onVersionChange(document.documentId, pipelineId, version)}
          />
        ),
      },
      {
        key: 'actions',
        header: '',
        className: 'w-12 text-right',
        render: (document) => (
          <ActionsCell
            document={document}
            onOpenDocumentManager={onOpenDocumentManager}
          />
        ),
      },
    ],
    [
      rendererEntries,
      analysisStatuses,
      availablePipelines,
      running,
      resolvedOrgId,
      wsId,
      basePath,
      apiClient,
      onPreviewRequest,
      onRunDocument,
      onRunComparison,
      onViewComparison,
      onOpenDocumentManager,
      pipelineSelections,
      onTogglePipelineSelection,
      onClearPipelineSelection,
      versionSelections,
      onVersionChange,
    ],
  )
}
